import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { of, switchMap, catchError } from 'rxjs';
import {
  SessionService,
  AuthService,
  StateService,
  TranslationService,
  LoggingService,
  ToastService,
  ProjectService
} from '@newroom-connect/library/services';
import { Level } from '@newroom-connect/library/enums';
import { IProjectSigninConfig } from '@newroom-connect/library/interfaces';

import { NavigationService } from '../../services/navigation.service';

/**
 * Guard that handles code-based login authentication.
 * Processes login codes from query parameters and authenticates users.
 *
 * @param route The activated route snapshot containing route parameters and query params.
 *
 * @returns Observable that resolves to true if code login succeeds, or UrlTree for redirect.
 */
export const CodeLoginGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const sessionService = inject(SessionService);
  const authService = inject(AuthService);
  const stateService = inject(StateService);
  const translationService = inject(TranslationService);
  const loggingService = inject(LoggingService);
  const toastService = inject(ToastService);
  const navigationService = inject(NavigationService);
  const projectService = inject(ProjectService);

  const code = route.children.at(0)?.queryParams['code'];
  const projectSlug = route.params['projectSlug'];
  const areaSlug = route.children.at(0)?.params['areaSlug'];

  // If no code is present, this guard doesn't apply
  if (!code || !projectSlug) {
    return of(false);
  }

  // Set progress to true so the UI shows loading state
  sessionService.setCodeLoginProgress(true);
  stateService.setLoadingState(true);

  // Create code login credentials with fallback code transformation
  const credentials = authService.getCodeLoginCredentials(projectSlug, code);

  loggingService.debug('Logging code user', credentials);

  // Perform the login
  return authService.login(credentials).pipe(
    switchMap(() => projectService.getProjectSigninConfig(projectSlug)),
    switchMap((projectSigninConfig: IProjectSigninConfig) => {
      // Priority for area redirection:
      //  1. Area Slug in Route.
      //  2. Session Redirect Area - Extracted from token.
      //  3. Project Start Area.
      loggingService.debug('Logged in with code user');
      const currentSession = sessionService.getUserSession();

      const redirectArea = areaSlug || currentSession?.getRedirectArea() || projectSigninConfig?.startAreaSlug;

      navigationService.navigateToAreas(projectSlug, redirectArea);

      stateService.setLoadingState(false);
      sessionService.setCodeLoginProgress(false);

      return of(true);
    }),
    catchError(error => {
      loggingService.error('Code login failed:', error);

      // Show error toast
      toastService.setMessage({
        message: translationService.translate('ERROR.CODE_LOGIN_FAILED.MESSAGE'),
        level: Level.ERROR
      });

      // Navigate to sign-in page on error
      navigationService.navigateToSignIn(projectSlug);
      return of(false);
    })
  );
};
